import { type GetHookResponse } from '../hooks'
import { gameKey, gameListKey } from '../keys'
import { useTantalimQuery } from '../query'
import { useProfile } from '../user'
import { toNewGameData } from './cache'
import { useQueryClient } from '@tanstack/react-query'
import { DAY, type GameData, type GameGetSuccess, type GameListInput, type GameListSuccess, HOUR } from '@terros/common'
import { useMemo } from 'react'

export function useGames(input?: GameListInput, enabled = true): GetHookResponse<GameData[]> {
  const queryClient = useQueryClient()
  const writeCache = (games: GameData[]): void => {
    for (const game of games) {
      queryClient.setQueryData<GameGetSuccess>(gameKey(game.gameId), (prev: GameGetSuccess | undefined) => {
        if (!prev) return
        return { ...prev, game: toNewGameData(game) }
      })
    }
  }

  return useTantalimQuery<GameListInput, GameListSuccess, GameData[]>({
    url: '/game/list',
    queryKey: gameListKey(input),
    input: input ?? {},
    convertResponse: (data) => {
      const newGames = data.games.map(toNewGameData)
      writeCache(newGames)
      return newGames
    },
    options: {
      gcTime: DAY,
      staleTime: 2 * HOUR,
      enabled,
    },
  })
}

export function useOwnGames(
  additionalFilter?: (game: GameData) => boolean,
  input?: GameListInput
): GetHookResponse<GameData[]> {
  const { data, ...rest } = useGames(input)
  const { user } = useProfile()
  const ownGames = useMemo(
    () =>
      data
        ?.filter((game) => {
          if (game.ownerId !== user?.userId) return false
          if (!additionalFilter) return true
          return additionalFilter(game)
        })
        .sort((a, b) => a.startDate - b.startDate),
    [data, user, additionalFilter]
  )
  return {
    data: ownGames,
    ...rest,
  }
}
